import {
  FluidButtonType,
  FluidComponentDefinition,
  FluidFormArrayControl,
  FluidFormArrayItem,
  FluidFormArrayTranslationOptions,
  FluidFormState,
  FluidIconConfig,
  FluidPopoverConfig,
  FluidTheme,
} from '@lmig/fluid-core';
import { FluidButtonTranslations } from '@lmig/fluid-core/lib/i18n/types/temp-element-translations';
import { h } from '@stencil/core';

import { InlineError } from '../../../common/fluid-common-warning.components';
import { FluidComponentFromDefinition } from '../../../common/fluid-component-rendering.components';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { themeVar } from '../../../utils/themes/theme.variables';
import { Utils } from '../../../utils/utils';

/**
 * Renders the button to add a new entry to the form array.
 */
export const AddForm = (props: {
  isLast: Function;
  form: FluidFormArrayItem;
  forms: FluidFormArrayItem[];
  disabledByState: boolean;
  addNewForm: Function;
  maxEntriesMet: boolean;
  buttonConfig?: FluidFormArrayControl;
  theme: FluidTheme;
  fluidTranslate?: FluidButtonTranslations;
}) => {
  const {
    isLast,
    form,
    forms,
    addNewForm,
    buttonConfig,
    maxEntriesMet,
    disabledByState,
    fluidTranslate,
    theme,
  } = props;
  const isDisabled = forms.filter((f) => f.active).some((f) => !f.state.valid);
  const iconConfig = themeVar<FluidIconConfig>(theme, 'icons.form_array.add');

  return (
    isLast(form.activeIdx, forms) && (
      <fluid-button
        type={FluidButtonType.GHOST}
        popover={buttonConfig?.popover}
        fluidTranslate={fluidTranslate}
        removeMargin={true}
        disabled={(isDisabled && disabledByState) || maxEntriesMet}
        onClick={() => addNewForm()}
        label={buttonConfig?.label || 'Add'}
        iconBefore={iconConfig}
        theme={theme}
        overrideGlobalTheme={true}
      />
    )
  );
};

/**
 * Renders the button to cancel the last entry added to the form array.
 */
export const CancelNewForm = (props: {
  showCancel: Function;
  form: FluidFormArrayItem;
  cancelLastForm: Function;
  buttonConfig?: FluidFormArrayControl;
  theme: FluidTheme;
  fluidTranslate?: FluidButtonTranslations;
}) => {
  const {
    showCancel,
    cancelLastForm,
    buttonConfig,
    form,
    theme,
    fluidTranslate,
  } = props;
  const iconConfig = themeVar<FluidIconConfig>(
    theme,
    'icons.form_array.delete'
  );

  return (
    showCancel(form) && (
      <div class={'cancel-container'}>
        <fluid-button
          key={`cancel-form-${form.activeIdx}`}
          type={FluidButtonType.GHOST}
          popover={buttonConfig?.popover}
          icon={!buttonConfig?.label && iconConfig}
          label={buttonConfig?.label}
          iconAfter={buttonConfig?.label && { key: iconConfig.key }}
          onClick={() => cancelLastForm()}
          removeMargin={true}
          fluidTranslate={fluidTranslate}
          theme={theme}
          overrideGlobalTheme={true}
        />
      </div>
    )
  );
};

/**
 * Renders the button to delete any previous entry in the form array,
 * except when constrained by array constraints.
 */
export type DeleteFormProps = {
  deleteForm: Function;
  form: FluidFormArrayItem;
  buttonConfig: FluidFormArrayControl;
  allowDeleteForm: boolean;
  theme: FluidTheme;
  fluidTranslate?: FluidButtonTranslations;
};

export const DeleteForm = (props: DeleteFormProps) => {
  const {
    form,
    deleteForm,
    buttonConfig,
    allowDeleteForm,
    theme,
    fluidTranslate,
  } = props;
  const iconConfig = themeVar<FluidIconConfig>(
    theme,
    'icons.form_array.delete'
  );

  return allowDeleteForm ? (
    <div class={'cancel-container'}>
      <fluid-button
        key={`delete-form-${form.activeIdx}`}
        type={FluidButtonType.GHOST}
        icon={!buttonConfig?.label && iconConfig}
        label={buttonConfig?.label}
        fluidTranslate={fluidTranslate}
        iconAfter={buttonConfig?.label && { key: iconConfig.key }}
        onClick={() => deleteForm(form.id)}
        removeMargin={true}
        theme={theme}
        overrideGlobalTheme={true}
      />
    </div>
  ) : null;
};

/**
 * Renders the entry title, executing any custom entryLabel function
 * with the index and entry value to return the label, otherwise,
 * uses the default 'Entry #x'.
 */
export const EntryLegend = (props: {
  entryLabel?: (index, formValue) => string;
  index: number;
  formState: FluidFormState;
  fluidTranslate?: FluidFormArrayTranslationOptions;
  i18nStore?: FluidI18nStore;
}) => {
  const { entryLabel, index, formState, fluidTranslate, i18nStore } = props;
  const entryTitle =
    i18nStore && !!fluidTranslate?.entryLegend
      ? i18nStore?.translate(fluidTranslate?.entryLegend, {
          index: index + 1,
          formState: formState.rawValue,
        })
      : entryLabel
      ? entryLabel(index + 1, formState.rawValue)
      : `Entry #${index + 1}`;

  return <legend>{entryTitle}</legend>;
};

/**
 * Renders the form element 'label' as a section title, with section description.
 */
export interface SectionTitleType {
  label: string;
  helpText: string | FluidPopoverConfig;
  sectionDetail?: string | FluidComponentDefinition | Function;
  theme: FluidTheme;
  formState?: FluidFormState;
  externalState?: Record<string, any>;
}

export const SectionTitle = (props: SectionTitleType) => {
  const { label, helpText, theme, formState, externalState } = props;
  let { sectionDetail } = props;
  let renderComponent;

  const currentSectionDetail = sectionDetail;

  if (typeof sectionDetail === 'string') {
    sectionDetail = () => ({
      component: 'fluid-text',
      props: {
        disableGutters: true,
      },
      content: currentSectionDetail,
    });
    renderComponent = sectionDetail().content;
  } else if (typeof sectionDetail === 'object') {
    renderComponent = (
      <FluidComponentFromDefinition
        definition={sectionDetail as FluidComponentDefinition}
      />
    );
  } else if (Utils.isFunction(sectionDetail)) {
    renderComponent = (
      <FluidComponentFromDefinition
        definition={
          sectionDetail(formState, externalState) as FluidComponentDefinition
        }
      />
    );
  }

  return (
    <fluid-section-detail
      class='form-array-section-detail'
      sectionTitle={label}
      titleLevel={4}
      infoPopover={helpText}
      theme={theme}
      overrideGlobalTheme={true}>
      {renderComponent}
    </fluid-section-detail>
  );
};

/**
 * Renders any errors in the form array.
 */
type FormErrorsProps = {
  showErrors: boolean;
  errors: any;
  onShowErrors: Function;
};
export const FormErrors = (props: FormErrorsProps) => {
  const { showErrors, errors, onShowErrors } = props;

  if (showErrors && errors.length > 0) {
    return errors.map(
      (error) =>
        error.message && (
          <InlineError
            show={true}
            error={error.message}
            onClick={onShowErrors}
          />
        )
    );
  }
};
